/**
 * PhoneField component
 * @author John Neale
 */

// Library
import { ClassAttributes, InputHTMLAttributes, useMemo } from 'react';
import { FieldHookConfig, useField } from 'formik';
import { ParsedCountry, PhoneInput } from 'react-international-phone';

// CSS Style for this module
import styles from './phoneFieldFormik.module.scss';

import 'react-international-phone/style.css';

/**
 * Interface for PhoneField props
 */
interface InputProps {
	props: InputHTMLAttributes<HTMLInputElement> &
		ClassAttributes<HTMLInputElement> &
		FieldHookConfig<string>;
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * Default country to show on the country dropdown
	 */
	defaultCountry?: string;
	/**
	 * Forces the country to the default country
	 */
	forceCountry?: boolean;

	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Phone fields let users enter and edit phone numbers.
 * @param {InputProps} Prop
 * @returns  an styled input element
 */
export const PhoneFieldFormik = ({
	customStyles,
	defaultCountry = 'au',
	forceCountry = false,
	testId,
	props,
}: InputProps) => {
	const [field, meta, getFieldHelpers] = useField(props);
	const hasError = useMemo(() => meta.error && meta.touched, [meta.error, meta.touched]);
	const inputStyles = useMemo(
		() =>
			[
				styles.phoneField,
				styles[`phoneField-disabled-${props.disabled}`],
				styles[`phoneField-${hasError ? 'error' : ''}`],
			].join(' '),
		[hasError, props.disabled],
	);

	const setValue = (value: string, meta: { country: ParsedCountry; inputValue: string }) => {
		// This is to stop the default country code dirtying the page.
		if (meta.inputValue.length == 0 && field.value.length == 0) {
			return;
		} else {
			getFieldHelpers.setValue(value);
		}
	};
	return props.hidden ? (
		<div className={[styles.container, customStyles].join(' ')}></div>
	) : (
		<div className={[styles.container, customStyles].join(' ')}>
			<PhoneInput
				className={inputStyles}
				defaultCountry={defaultCountry}
				value={field.value}
				onChange={(phone, meta) => setValue(phone, meta)}
				inputClassName={styles['phoneInputField']}
				hideDropdown={forceCountry}
				disableDialCodeAndPrefix={forceCountry}
				disableDialCodePrefill={!forceCountry}
				data-testId={testId}
			/>
			{hasError && <p className={styles['error-msg']}>{meta.error}</p>}
		</div>
	);
};
